import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTags, deleteTag } from "@/services/firebase/tag";
import { Tag } from "@/types/tag";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { useDebounce } from "@/hooks/useDebounce";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminDataTable,
  AdminLoadingSkeleton,
  AdminErrorAlert,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export default function TagListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [primaryLanguage, setPrimaryLanguage] = useState<"tw" | "zh" | "en" | "ms">("tw");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    name: string;
  }>({ isOpen: false, id: null, name: "" });

  const { data: tags, isLoading, error } = useQuery({
    queryKey: ["tags"],
    queryFn: () => getTags(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] });
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
      setDeleteError(null);
    },
    onError: (error) => {
      console.error("刪除標籤失敗:", error);
      setDeleteError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, name: "" });
    },
  });

  // 過濾和搜尋（使用防抖後的搜尋詞）
  const filteredTags = useMemo(() => {
    if (!tags || tags.length === 0) return [];

    return tags.filter((tag) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      const matchesSearch =
        !searchLower ||
        tag.id.toLowerCase().includes(searchLower) ||
        (tag.tag_id && tag.tag_id.toLowerCase().includes(searchLower)) ||
        (tag.tw && tag.tw.toLowerCase().includes(searchLower)) ||
        (tag.zh && tag.zh.toLowerCase().includes(searchLower)) ||
        (tag.en && tag.en.toLowerCase().includes(searchLower)) ||
        (tag.ms && tag.ms.toLowerCase().includes(searchLower));

      return matchesSearch;
    });
  }, [tags, debouncedSearchTerm]);

  const handleDelete = (id: string, name: string) => {
    setDeleteConfirm({ isOpen: true, id, name });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  const getDisplayName = (tag: Tag): string => {
    return tag[primaryLanguage] || tag.tw || tag.zh || tag.en || tag.ms || tag.id;
  };

  const columns: AdminTableColumn[] = [
    { key: "tag_id", label: "Tag ID" },
    { key: "primary", label: "主要顯示" },
    { key: "tw", label: "繁體中文", hideBelow: "md" },
    { key: "zh", label: "簡體中文", hideBelow: "md" },
    { key: "en", label: "英文", hideBelow: "lg" },
    { key: "ms", label: "馬來文", hideBelow: "lg" },
    { key: "created_at", label: "建立時間", hideBelow: "xl" },
    { key: "actions", label: "操作" },
  ];

  const renderCell = (item: Tag, col: AdminTableColumn) => {
    switch (col.key) {
      case "tag_id":
        return <span className="font-mono text-sm">{item.tag_id || item.id}</span>;
      case "primary":
        return <span className="font-semibold text-foreground">{getDisplayName(item)}</span>;
      case "tw":
        return <span className="text-muted-foreground">{item.tw || "-"}</span>;
      case "zh":
        return <span className="text-muted-foreground">{item.zh || "-"}</span>;
      case "en":
        return <span className="text-muted-foreground">{item.en || "-"}</span>;
      case "ms":
        return <span className="text-muted-foreground">{item.ms || "-"}</span>;
      case "created_at":
        return (
          <span className="text-muted-foreground">
            {item.created_at || item.createdAt
              ? new Date(item.created_at || item.createdAt!).toLocaleString("zh-TW")
              : "-"}
          </span>
        );
      case "actions":
        return (
          <ActionMenu
            actions={[
              {
                label: "編輯",
                onClick: () => navigate(`/tags/${item.id}/edit`),
              },
              {
                label: "刪除",
                onClick: () => handleDelete(item.id, getDisplayName(item)),
                disabled: deleteMutation.isPending,
                variant: "danger",
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="標籤管理" createHref="/tags/new" createLabel="新增標籤" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="標籤管理" createHref="/tags/new" createLabel="新增標籤" />
        <AdminErrorAlert
          message={`載入標籤時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="標籤管理"
        createHref="/tags/new"
        createLabel="新增標籤"
        actions={
          <div className="flex items-center gap-3">
            <AdminSearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="搜尋標籤 ID 或各語言名稱..."
              className="max-w-md"
            />
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">
                主要語言：
              </label>
              <Select
                value={primaryLanguage}
                onValueChange={(v) => setPrimaryLanguage(v as "tw" | "zh" | "en" | "ms")}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tw">繁體中文</SelectItem>
                  <SelectItem value="zh">簡體中文</SelectItem>
                  <SelectItem value="en">英文</SelectItem>
                  <SelectItem value="ms">馬來文</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
      />

      {deleteError && (
        <AdminErrorAlert
          message={`刪除標籤失敗：${deleteError}`}
          onDismiss={() => setDeleteError(null)}
        />
      )}

      <AdminDataTable
        columns={columns}
        data={filteredTags}
        loading={false}
        renderCell={renderCell}
        getRowId={(item) => item.id}
        emptyTitle={searchTerm ? "沒有符合條件的標籤" : "尚無標籤資料"}
        emptyActionHref={searchTerm ? undefined : "/tags/new"}
        emptyActionLabel="新增第一個標籤"
        pageSize={20}
      />

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除標籤「${deleteConfirm.name}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: "" })}
      />
    </AdminPage>
  );
}
